import { type Control, type FieldErrors, type FieldValues } from "react-hook-form";

import { type JsonSchemaProperty } from "../../types";

export interface SchemaFieldProps {
  fieldKey: string;
  property: JsonSchemaProperty;
  fieldPath: string;
  isRequired: boolean;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
}

export function getNestedError(errors: FieldErrors<FieldValues>, path: string): string | undefined {
  const parts = path.split(".");
  let current: unknown = errors;

  for (const part of parts) {
    if (current === undefined || current === null || typeof current !== "object") {
      return undefined;
    }

    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current === "object" && current !== null && "message" in current) {
    return (current as { message?: string }).message;
  }

  return undefined;
}

export function isDateProperty(property: JsonSchemaProperty): boolean {
  return property.type === "string" && property.format === "date";
}

export function isNumberProperty(property: JsonSchemaProperty): boolean {
  return property.type === "number" || property.type === "integer";
}

export function isEnumProperty(property: JsonSchemaProperty): boolean {
  return Array.isArray(property.enum) && property.enum.length > 0;
}

export function isObjectProperty(property: JsonSchemaProperty): boolean {
  return property.type === "object" && property.properties !== undefined;
}

export function isBooleanProperty(property: JsonSchemaProperty): boolean {
  return property.type === "boolean";
}

export function buildFieldIds(fieldPath: string, hasDescription: boolean) {
  const safePath = fieldPath.replace(/[^a-zA-Z0-9_-]/g, "-");
  const id = `field-${safePath}`;
  const describedBy = hasDescription ? `${id}-desc` : undefined;
  return { id, describedBy };
}

export function hasNonEmptyDescription(description: string | undefined): boolean {
  return typeof description === "string" && description.trim().length > 0;
}
